import cls from 'clsx';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { RadioGroup } from '@/components/atoms/RadioSwitch';
import { RadioInput } from '@/components/atoms/RadioSwitch/RadioInput';
import { Span } from '@/components/atoms/Typography/Typography';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import {
  formFields,
  formValues,
  FormValues,
} from '@/containers/ConfigurePage/components/FormConfig/formData';
import { useData } from '@/lib/pageData/pageDataContext';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import {
  ProductSubscriptionByRegionResponse,
  SubscriptionType,
} from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { formatPrice } from '@/utils/formatPrice';
import { getCurrentSubscriptionType } from '../../utils/getCurrentSubscriptionType';
import styles from './ChooseMembership.module.scss';

interface Props {
  hasSpecialOffer: boolean;
  product: TransformedAssetCategoryResult;
  subscriptions: ProductSubscriptionByRegionResponse;
}

const getOneOffFee = (subscriptions: SubscriptionType[]) =>
  subscriptions.find(item => item.isSpecialOffer)?.oneOffFee ??
  subscriptions?.[0]?.oneOffFee;

export const ChooseMembership = ({
  subscriptions,
  hasSpecialOffer,
  product,
}: Props) => {
  const { locale } = useRouter();
  const { currency } = useData<'default'>();
  const { control } = useFormContext<FormValues>();
  const { t } = useTranslation(nameSpacesConfig.pages);
  const watchedFormValues = useWatch<FormValues>({
    defaultValue: {
      durationMonths: '6',
      student: 'no',
      heavyUse: 'no',
    },
  });

  const currentType = getCurrentSubscriptionType(watchedFormValues, product);

  const oneOffLoyalSubsriptions = subscriptions.filter(
    item =>
      item.customerType === currentType &&
      !item.isFlexible &&
      item.minDurationMonths === 6
  );
  const oneOffFlexibleSubscriptions = subscriptions.filter(
    item =>
      item.customerType === currentType &&
      item.isFlexible &&
      item.minDurationMonths === 1
  );
  const oneOffLoyalPrice = getOneOffFee(oneOffLoyalSubsriptions);
  const oneOffFlexiblePrice = getOneOffFee(oneOffFlexibleSubscriptions);

  return (
    <Controller
      control={control}
      name={formFields.durationMonths}
      render={({ field }) => (
        <RadioGroup
          label={t('configure.membership.label')}
          version="level3"
          className={styles.group}
          onChange={field.onChange}
          value={field.value}
          name={field.name}
        >
          {hasSpecialOffer && (
            <div
              className={cls(styles.membershipSpecialLabel, {
                [styles.disabled]: watchedFormValues.durationMonths !== '6',
              })}
            >
              <span>
                {t(
                  `configure.membership.specialOffer.${product.productConfig.translationKey}.label`
                )}
              </span>
            </div>
          )}
          <RadioInput
            aria-label={t('configure.membership.button1')}
            className={styles.radioButton}
            value={formValues.durationMonths[6]}
          >
            <div className={styles.radioText}>
              <div className={styles.membership}>
                <span>{t('configure.membership.button1')}</span>
                <span translate="no">
                  {t('configure.membership.oneOff', {
                    price: formatPrice(oneOffLoyalPrice, locale!, currency),
                  })}
                </span>
              </div>
              <Span>{t('configure.membership.text1-2')}</Span>
            </div>
          </RadioInput>
          <RadioInput
            aria-label={t('configure.membership.button2')}
            value={formValues.durationMonths[1]}
            className={styles.radioButton}
          >
            <div className={styles.radioText}>
              <div className={styles.membership}>
                <span>{t('configure.membership.button2')}</span>
                <span translate="no">
                  {t('configure.membership.oneOff', {
                    price: formatPrice(oneOffFlexiblePrice, locale!, currency),
                  })}
                </span>
              </div>
              <Span>{t('configure.membership.text2-2')}</Span>
            </div>
          </RadioInput>
          {watchedFormValues.durationMonths !== '6' && hasSpecialOffer && (
            <Span className={styles.promoMissingOut} version="body1">
              <Trans
                components={{
                  bold: <strong />,
                }}
                i18nKey={`pages:configure.membership.specialOffer.${product.productConfig.translationKey}.flexible.label`}
              />
            </Span>
          )}
        </RadioGroup>
      )}
    />
  );
};
